import { useState, useEffect } from "react"

/**
 * Timer component that counts down from a specified time and updates the displayed time in hours, minutes, and seconds.
 * Once the timer reaches zero, the `onFinish` callback is triggered.
 * 
 * @param {number} timeLeft - The time in milliseconds from which to start the countdown.
 * @param {function} onFinish - A callback function to execute when the countdown reaches zero.
 * 
 * @returns {Object} An object containing the current countdown time in hours, minutes, and seconds.
 * 
 * @example
 * const timeLeft = Date.now() + 60000 // Countdown for 1 minute from the current time
 * const handleFinish = () => alert("Timer finished!")
 * const { hours, minutes, seconds } = Timer(timeLeft, handleFinish)
 */
const Timer = ({ initialTimeLeft, onFinish }) => {
  const [timeDisplay, setTimeDisplay] = useState({
    hours: "---",
    minutes: "---",
    seconds: "---",
  });

  useEffect(() => {
    if (!initialTimeLeft) return;

    const updateTime = () => {
      const timeRemaining = initialTimeLeft - Date.now();

      if (timeRemaining <= 0) {
        setTimeDisplay({ hours: 0, minutes: 0, seconds: 0 });
        clearInterval(intervalId);
        onFinish();
      } else {
        const hours = Math.floor(timeRemaining / (1000 * 60 * 60));
        let minutes = Math.floor((timeRemaining % (1000 * 60 * 60)) / (1000 * 60));
        let seconds = Math.floor((timeRemaining % (1000 * 60)) / 1000);

        if (seconds === 60) {
          seconds = 0;
          minutes += 1;
        }

        setTimeDisplay({ hours, minutes, seconds });
      }
    };

    const intervalId = setInterval(updateTime, 1000);

    updateTime();

    return () => clearInterval(intervalId);
  }, [initialTimeLeft, onFinish]);

  return (
    <span>
      {timeDisplay.hours === "---"
        ? "Calculating..."
        : `${timeDisplay.hours}h ${timeDisplay.minutes}m ${timeDisplay.seconds}s`}
    </span>
  );
};

export default Timer;